/**
 * Nav/route SCOPE model (ADR-009 D7 — "one portal, scope-aware menus").
 *
 * Every nav item / guarded route is tagged with a plane:
 *   - 'platform' — platform-administration plane (Tenants, Configuration)
 *   - 'tenant'   — a specific tenant's assets (Agents, Knowledge Banks, …)
 *   - 'both'     — meaningful in either plane (Dashboard, Users, Audit Log)
 *
 * Visibility layers ON TOP of the existing `requiredPermission` filter; it is
 * NOT the security boundary. The real guard is the backend
 * (`Permissions.TenantsManage` is platformAdmin-only + ADR-008 claim binding
 * 403s a cross-plane call). This helper only decides what the UI shows and
 * which routes render a clean Forbidden instead of firing a doomed request
 * (ADR-009 D7, review note S3).
 */
export type NavScope = 'platform' | 'tenant' | 'both';

/**
 * Scope-visibility rule shared by the Sidebar nav filter and the
 * <RequireScope> route guard so the two can never drift.
 *
 * @param scope             the item/route plane
 * @param isPlatformAdmin   caller is a platformAdmin
 * @param hasTenantSelected a tenant is selected in the platform picker
 *
 * Rules:
 *   - platformAdmin WITH a tenant selected (impersonation) → sees everything
 *   - platformAdmin WITHOUT a tenant selected → platform + both only
 *   - tenant roles (no picker) → tenant + both
 */
export function scopeVisible(
  scope: NavScope,
  isPlatformAdmin: boolean,
  hasTenantSelected: boolean,
): boolean {
  if (isPlatformAdmin) {
    return hasTenantSelected || scope === 'platform' || scope === 'both';
  }
  return scope === 'tenant' || scope === 'both';
}
